import { modalAnatomy as parts } from '@chakra-ui/anatomy'
import type {
  PartsStyleFunction,
  PartsStyleObject,
  SystemStyleFunction,
  SystemStyleObject,
} from '@chakra-ui/theme-tools'
import { mode } from '@chakra-ui/theme-tools'
import { colors } from '../styles'

const baseStyleOverlay: SystemStyleObject = {
  bg: 'blackAlpha.600',
  zIndex: 'modal',
}

const baseStyleDialogContainer: SystemStyleFunction = props => {
  const { isCentered, scrollBehavior } = props

  return {
    display: 'flex',
    zIndex: 'modal',
    justifyContent: 'center',
    alignItems: isCentered ? 'center' : 'flex-start',
    overflow: scrollBehavior === 'inside' ? 'hidden' : 'auto',
  }
}

const baseStyleDialog: SystemStyleFunction = props => {
  const { scrollBehavior } = props

  return {
    borderRadius: 'md',
    bg: mode('white', 'gray.700')(props),
    color: 'inherit',
    my: '3.75rem',
    pt: 0,
    pr: 0,
    pl: 0,
    pb: 0,
    zIndex: 'modal',
    maxH: scrollBehavior === 'inside' ? 'calc(100% - 7.5rem)' : undefined,
    boxShadow: mode('lg', 'dark-lg')(props),
  }
}

const baseStyleHeader: SystemStyleObject = {
  px: 8,
  pt: 6,
  pb: 6,
  fontSize: 'xl',
  fontWeight: 'semibold',
  borderBottom: `1px solid ${colors.appGrey}`,
}

const baseStyleCloseButton: SystemStyleObject = {
  position: 'absolute',
  top: '18px',
  insetEnd: 6,
  mt: 0,
  mr: 2,
}

const baseStyleBody: SystemStyleFunction = props => {
  const { scrollBehavior } = props
  return {
    px: 8,
    pt: 4,
    pb: 12,
    flex: 1,
    overflow: scrollBehavior === 'inside' ? 'auto' : undefined,
  }
}

const baseStyleFooter: SystemStyleObject = {
  px: 8,
  py: 6,
}

const baseStyle: PartsStyleFunction<typeof parts> = props => ({
  overlay: baseStyleOverlay,
  dialogContainer: baseStyleDialogContainer(props),
  dialog: baseStyleDialog(props),
  header: baseStyleHeader,
  closeButton: baseStyleCloseButton,
  body: baseStyleBody(props),
  footer: baseStyleFooter,
})

/**
 * Since the `maxWidth` prop references theme.sizes internally,
 * we can leverage that to size our modals.
 */
function getSize(value: string): PartsStyleObject<typeof parts> {
  if (value === 'full') {
    return {
      dialog: { maxW: '100vw', minH: '100vh', my: 0 },
    }
  }
  return {
    dialog: { maxW: value },
  }
}

const sizes = {
  xs: getSize('xs'),
  sm: getSize('sm'),
  md: getSize('md'),
  lg: getSize('lg'),
  xl: getSize('xl'),
  '2xl': getSize('2xl'),
  '3xl': getSize('3xl'),
  '4xl': getSize('4xl'),
  '5xl': getSize('5xl'),
  '6xl': getSize('6xl'),
  full: getSize('full'),
}

const defaultProps = {
  size: 'md',
}

export default {
  parts: parts.keys,
  baseStyle,
  sizes,
  defaultProps,
}
